import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import type { SelectGroup, SelectOption } from '../components';

// Religion with metadata for dynamic form logic
export interface ReligionWithMeta {
  religion_id: string;
  name: string;
  have_sect: boolean;
  have_caste: boolean;
  have_manglik: boolean;
}

// Sect with metadata for jamat logic
export interface SectWithMeta {
  sect_id: string;
  name: string;
  have_jamat: boolean;
}

export interface UserLocationResponse {
  userLocation: string;
}

// Helper to extract data from API response (handles both wrapped and direct responses)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractData = <T>(response: any): T[] => {
  if (!response) return [];
  // If response has 'data' property, use it; otherwise assume response is the array
  if (response.data !== undefined) {
    return response.data || [];
  }
  return Array.isArray(response) ? response : [];
};

/**
 * Shared Service - Handles all shared/lookup data APIs
 * Data is loaded at app initialization and stored in Zustand
 * API Documentation: https://staging.api.saathi4u.com/swagger-doc#/
 */
export class SharedService extends ApiService {
  
  // ============ Basic Lookups ============
  
  /**
   * Get all heights
   */
  async getHeights(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.heights);
    const data = extractData<{ height_id: string; height: string }>(response);
    return data.map((item) => ({
      value: item.height_id,
      label: item.height,
    }));
  }

  /**
   * Get all mother tongues (grouped by region)
   */
  async getMotherTongues(): Promise<SelectGroup[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.motherTongues);
    const data = extractData<{ label: string; options: { label: string; value: string }[] }>(response);
    return data;
  }

  /**
   * Religion metadata interface
   */
  

  /**
   * Get all religions (simple format for dropdowns)
   */
  async getReligions(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.religions);
    const data = extractData<{ religion_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.religion_id,
      label: item.name,
    }));
  }

  /**
   * Get all religions with metadata (have_sect, have_caste, have_manglik)
   * Used for dynamic form logic
   */
  async getReligionsWithMeta(): Promise<ReligionWithMeta[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.religions);
    const data = extractData<ReligionWithMeta>(response);
    return data;
  }

  /**
   * Get castes by religion ID (optionally filtered by sect_id)
   */
  async getCastes(religionId: string, sectId?: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.castes(religionId, sectId));
    const data = extractData<{ caste_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.caste_id,
      label: item.name,
    }));
  }

  /**
   * Get sub-castes by caste ID
   */
  async getSubCastes(casteId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.subCastes(casteId));
    const data = extractData<{ sub_caste_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.sub_caste_id,
      label: item.name,
    }));
  }

  /**
   * Get sects by religion ID (simple format for dropdowns)
   */
  async getSects(religionId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.sects(religionId));
    const data = extractData<{ sect_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.sect_id,
      label: item.name,
    }));
  }

  /**
   * Get sects with metadata (have_jamat)
   * Used for dynamic form logic
   */
  async getSectsWithMeta(religionId: string): Promise<SectWithMeta[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.sects(religionId));
    const data = extractData<SectWithMeta>(response);
    return data;
  }

  /**
   * Get jamats by sect ID
   */
  async getJamats(sectId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.jamats(sectId));
    const data = extractData<{ jamat_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.jamat_id,
      label: item.name,
    }));
  }

  /**
   * Get gotras by religion ID
   */
  async getGotras(religionId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.gotras(religionId));
    const data = extractData<{ gotra_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.gotra_id,
      label: item.name,
    }));
  }

  // ============ Location Lookups ============

  /**
   * Get all countries
   */
  async getCountries(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.countries);
    const data = extractData<{ country_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.country_id,
      label: item.name,
    }));
  }

  /**
   * Get states by country ID
   */
  async getStates(countryId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.states(countryId));
    const data = extractData<{ state_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.state_id,
      label: item.name,
    }));
  }

  /**
   * Get cities by state ID
   */
  async getCities(stateId: string): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.cities(stateId));
    const data = extractData<{ city_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.city_id,
      label: item.name,
    }));
  }

  /**
   * Get user location for regional pricing
   * GET /shared/get-location
   */
  async getUserLocation(): Promise<UserLocationResponse> {
    return this.get<UserLocationResponse>(API_ENDPOINTS.shared.userLocation);
  }

  // ============ Personal Details Lookups ============

  /**
   * Get all marital statuses
   */
  async getMaritalStatuses(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.maritalStatuses);
    const data = extractData<{ marital_status_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.marital_status_id,
      label: item.name,
    }));
  }

  /**
   * Get all complexions
   */
  async getComplexions(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.complexions);
    const data = extractData<{ complexion_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.complexion_id,
      label: item.name,
    }));
  }

  /**
   * Get all body types
   */
  async getBodyTypes(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.bodyTypes);
    const data = extractData<{ body_type_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.body_type_id,
      label: item.name,
    }));
  }

  /**
   * Get all disabilities
   */
  async getDisabilities(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.disabilities);
    const data = extractData<{ disability_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.disability_id,
      label: item.name,
    }));
  }

  /**
   * Get all blood groups
   */
  async getBloodGroups(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.bloodGroups);
    const data = extractData<{ blood_group_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.blood_group_id,
      label: item.name,
    }));
  }

  // ============ Income & Education Lookups ============

  /**
   * Get all annual income ranges
   */
  async getCurrencies(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.incomeCurrencies);
    const data = extractData<{ income_currency_id: string; name: string; symbol: string }>(response);
    return data.map((item) => ({
      value: item.income_currency_id,
      label: item.name,
      displayLabel: item.symbol,
    }));
  }

  /**
   * Get all degrees (grouped by category)
   */
  async getDegrees(): Promise<SelectGroup[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.degrees);
    const data = extractData<{ label: string; options: { label: string; value: string }[] }>(response);
    return data;
  }

  /**
   * Get all employed in options
   */
  async getEmployedIn(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.employedIn);
    const data = extractData<{ employed_in_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.employed_in_id,
      label: item.name,
    }));
  }

  /**
   * Get occupations by employed in ID (grouped by category)
   */
  async getOccupations(employedInId: string): Promise<SelectGroup[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.occupations(employedInId));
    const data = extractData<{ label: string; options: { label: string; value: string }[] }>(response);
    return data;
  }

  // ============ Family Lookups ============

  /**
   * Get all family types
   */
  async getFamilyTypes(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.familyTypes);
    const data = extractData<{ family_type_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.family_type_id,
      label: item.name,
    }));
  }

  /**
   * Get all family values
   */
  async getFamilyValues(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.familyValues);
    const data = extractData<{ family_value_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.family_value_id,
      label: item.name,
    }));
  }

  /**
   * Get all family statuses
   */
  async getFamilyStatuses(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.familyStatuses);
    const data = extractData<{ family_status_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.family_status_id,
      label: item.name,
    }));
  }

  /**
   * Get all father occupations
   */
  async getFatherOccupations(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.fatherOccupations);
    const data = extractData<{ father_occupation_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.father_occupation_id,
      label: item.name,
    }));
  }

  /**
   * Get all mother occupations
   */
  async getMotherOccupations(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.motherOccupations);
    const data = extractData<{ mother_occupation_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.mother_occupation_id,
      label: item.name,
    }));
  }

  // ============ Lifestyle Lookups ============

  /**
   * Get all diets
   */
  async getDiets(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.diets);
    const data = extractData<{ diet_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.diet_id,
      label: item.name,
    }));
  }

  /**
   * Get all drinking habits
   */
  async getDrinkingHabits(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.drinkingHabits);
    const data = extractData<{ drinking_habit_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.drinking_habit_id,
      label: item.name,
    }));
  }

  /**
   * Get all smoking habits
   */
  async getSmokingHabits(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.smokingHabits);
    const data = extractData<{ smoking_habit_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.smoking_habit_id,
      label: item.name,
    }));
  }

  // ============ Horoscope Lookups ============

  /**
   * Get all stars
   */
  async getStars(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.stars);
    const data = extractData<{ star_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.star_id,
      label: item.name,
    }));
  }

  /**
   * Get all raasis (zodiac signs)
   */
  async getRaasis(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.raasis);
    const data = extractData<{ raasi_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.raasi_id,
      label: item.name,
    }));
  }

  /**
   * Get all doshas
   */
  async getDoshas(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.doshas);
    const data = extractData<{ dosha_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.dosha_id,
      label: item.name,
    }));
  }

  /**
   * Get all manglik statuses
   */
  async getManglikStatuses(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.manglikStatuses);
    const data = extractData<{ manglik_status_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.manglik_status_id,
      label: item.name,
    }));
  }

  // ============ Profile Lookups ============

  /**
   * Get all profile created for options
   */
  async getProfileCreatedFor(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.profileCreatedFor);
    const data = extractData<{ profile_created_for_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.profile_created_for_id,
      label: item.name,
    }));
  }

  /**
   * Get all hobbies
   */
  async getHobbies(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.hobbies);
    const data = extractData<{ hobby_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.hobby_id,
      label: item.name,
    }));
  }

  /**
   * Get all interests
   */
  async getInterests(): Promise<SelectOption[]> {
    const response = await this.get<unknown>(API_ENDPOINTS.shared.interests);
    const data = extractData<{ interest_id: string; name: string }>(response);
    return data.map((item) => ({
      value: item.interest_id,
      label: item.name,
    }));
  }

  // ============ File Upload ============

  /**
   * Uploaded file response from API
   */
  

  /**
   * Upload files to S3 via shared API
   * @param files - Array of File objects to upload
   * @returns Array of uploaded file info (id and url)
   */
  async uploadFiles(files: File[]): Promise<UploadedFile[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    interface UploadResult {
      success: boolean;
      fileId: string;
      originalName: string;
      fileName: string;
      fileUrl: string;
      fileSize: number;
      mimeType: string;
    }

    // Response structure after extractData: { results: [...], statistics: {...} }
    interface ExtractedUploadResponse {
      results: UploadResult[];
      statistics: {
        total: number;
        successful: number;
        failed: number;
        totalSize: number;
      };
    }

    const response = await this.post<ExtractedUploadResponse>(
      API_ENDPOINTS.shared.uploadFiles,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    console.log('[SharedService] uploadFiles response:', response);

    // ApiService.extractData already extracts the 'data' property
    // So response is: { results: [...], statistics: {...} }
    const results = response.results || [];
    
    return results
      .filter((result: UploadResult) => result.success)
      .map((result: UploadResult) => ({
        id: parseInt(result.fileId, 10),
        url: result.fileUrl,
        fileName: result.fileName || '',
        originalName: result.originalName || '',
      }));
  }

  /**
   * Associate uploaded file with user profile (main photo)
   * @param fileId - File ID from upload response
   * @returns Success response
   */
  async uploadUserImage(fileId: number): Promise<{ message: string }> {
    console.log('[SharedService] uploadUserImage called with fileId:', fileId);
    console.log('[SharedService] API endpoint:', API_ENDPOINTS.shared.uploadUserImage);
    const response = await this.post<{ message: string }>(
      API_ENDPOINTS.shared.uploadUserImage,
      { file_id: fileId.toString() }
    );
    console.log('[SharedService] uploadUserImage response:', response);
    return response;
  }

  /**
   * Associate uploaded files with user profile (gallery photos)
   * @param fileIds - Array of File IDs from upload response
   * @returns Success response
   */
  async uploadUserOtherImages(fileIds: number[]): Promise<{ message: string }> {
    console.log('[SharedService] uploadUserOtherImages called with fileIds:', fileIds);
    console.log('[SharedService] API endpoint:', API_ENDPOINTS.shared.uploadUserOtherImages);
    const response = await this.post<{ message: string }>(
      API_ENDPOINTS.shared.uploadUserOtherImages,
      { file_ids: fileIds.map(id => id.toString()) }
    );
    console.log('[SharedService] uploadUserOtherImages response:', response);
    return response;
  }
}

export interface UploadedFile {
  id: number;
  url: string;
  fileName?: string;
  originalName?: string;
}

// Export singleton instance
export const sharedService = new SharedService();
